import { EventEmitter2 } from 'eventemitter2';
import { EventDetail } from '../../types';
import { getRojoDependencyClass, rojoCustomElement } from '../../lib/dependencies';

declare global {
    var Coral: any;
}

getRojoDependencyClass('rojo-page').then((pageClass) => {
    /**
     * CoralThread is a Web Component responsible for loading a Coral Thread.
     * It assumes there is only one thread on a page. It does not watch
     * for changes to attributes.
     *
     * @example
     *    <rojo-coral-thread root-URL="" story-ID="" site-domain="" base-path="" hubpages-ID="" css-url=""></rojo-coral-thread>
     */

    class CoralThread extends pageClass.RojoElement {
        embed: any;

        get coralToken() {
            return this.getCookieValue('mct');
        }

        get userToken() {
            return this.getCookieValue('mut');
        }

        get coralURL() {
            return this.getAttribute('root-URL') + `/assets/js/embed.js`;
        }

        get storyURL() {
            const basePath = this.getAttribute('base-path') ?? '';
            return (
                this.getAttribute('site-domain') +
                basePath +
                '/.api/item-redirect/' +
                this.getAttribute('story-ID')
            );
        }

        get storyHubpagesID() {
            const hubpagesID = this.getAttribute('hubpages-ID') ?? '';
            return hubpagesID.length > 0 ? hubpagesID : null;
        }

        _createEmbed() {
            const coralOptions = {
                id: 'coral_thread',
                autoRender: true,
                rootURL: this.getAttribute('root-URL'),
                storyID: this.getAttribute('story-ID'),
                storyURL: this.storyURL,
                storyHubpagesID: this.storyHubpagesID,
                events: this.handleCoralEvents.bind(this),
                refreshAccessToken: this.refreshAccessToken.bind(this),
                containerClassName: 'rojo-coral',
                customCSSURL: this.getAttribute('css-url'),
            } as any;
            if (this.coralToken) {
                coralOptions.accessToken = this.coralToken;
            }
            this.embed = globalThis.Coral.createStreamEmbed(coralOptions);
        }

        handleCoralEvents(events: EventEmitter2) {
            events.onAny((eventName: string | string[]) => {
                switch (eventName) {
                    case 'loginPrompt':
                        this.emitEvent('rojo:coral', 'login');
                        break;
                    case 'signOut.success':
                        this.emitEvent('rojo:coral', 'logout');
                        break;
                }
            });
        }

        _appendContainerAndScript() {
            const containerId = 'coral_thread';
            const div = document.createElement('div');
            div.id = containerId;
            this.appendChild(div);

            let script = document.createElement('script');
            script.id = 'coral-thread';
            script.src = this.coralURL;
            script.onload = this._createEmbed.bind(this);
            document.head.appendChild(script);
        }

        override connectedCallback() {
            super.connectedCallback();
            this._addListeners();
            this._appendContainerAndScript();
        }

        override disconnectedCallback() {
            super.disconnectedCallback();
            this._removeListeners();
        }

        _addListeners() {
            window.addEventListener(
                'log-event',
                this._handleUserStates.bind(this),
                pageClass.util?.supportPassiveEventListeners() ? { passive: true } : false
            );
        }

        _removeListeners() {
            window.removeEventListener('log-event', this._handleUserStates.bind(this));
        }

        _handleUserStates(e: CustomEventInit<EventDetail>) {
            const eventValue = e.detail?.value;

            switch (eventValue) {
                case 'userLoginCompleted':
                    if (this.coralToken && this.embed) {
                        this.embed.login(this.coralToken);
                    }
                    break;
                case 'userLogoutCompleted':
                    if (this.embed) {
                        this.embed.logout();
                    }
                    break;
            }
        }

        refreshAccessToken(nextAccessToken: (token: string) => void) {
            nextAccessToken(this.coralToken);
        }
    }

    rojoCustomElement('rojo-coral-thread', CoralThread);
});
